import { isLocalEnvironment } from 'utils/environment';
import { COOKIE_KEY as DEVICE_ID_COOKIE } from '@1debit/device-intelligence';
import { ANON_ID_COOKIE } from './cookies';

const DEFAULT_SUBDOMAIN = 'www'; // Default subdomain for production (enforced via redirects)

export const ALLOWED_OVERRIDE_SUBDOMAINS = ['enterprise', 'careers'];
const NON_PROD_SUBDOMAIN_SUFFIX_REGEX = /-(qa|dev\d+)$/;

export interface PageContext {
  subdomain: string;
  shouldOverrideSubdomain: boolean;
  url: string;
  referrer: string;
  userAgent: string;
  ip: string;
  nonce: string;
  path: string;
  anonymousId: string;
  segmentAnonymousId: string;
}

/**
 * Gets the current page context including URL and subdomain information
 * Ensures the URL is always an absolute URL
 */
export const getPageContext = async (): Promise<PageContext> => {
  // importing headers here in-line allows
  // this function to be used both client + server-side
  const { headers, cookies } = await import('next/headers');
  const headersList = await headers();
  const cookieStore = await cookies();

  const hostname = headersList.get('host') || '';
  const headerUrl = headersList.get('x-url') || '';
  const referrer = headersList.get('referer') || '';
  const userAgent = headersList.get('user-agent') || '';
  const ip = headersList.get('CF-Connecting-IP') || headersList.get('x-forwarded-for') || '';
  const subdomain = getValidSubdomainName(hostname);
  const nonce = headersList.get('x-nonce') || '';
  const path = headersList.get('x-path') || '/';

  const anonymousId = cookieStore.get(DEVICE_ID_COOKIE)?.value || ''; // Chime device id used in Hawker events
  const segmentAnonymousId = cookieStore.get(ANON_ID_COOKIE)?.value || '';

  // Ensure we always have a valid absolute URL
  let url = headerUrl;

  if (!url && typeof window !== 'undefined') {
    // If we're on the client and don't have a header URL, use window.location
    url = window.location.href;
  } else if (!url) {
    // If we still don't have a URL, construct one from the host
    const protocol = isLocalEnvironment() ? 'http' : 'https';
    // Ensure path starts with a slash
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    // Remove port from hostname if not local
    const hostParts = hostname.split(':');
    const cleanHostname = !isLocalEnvironment() ? hostParts[0] : hostname;

    url = `${protocol}://${cleanHostname}${normalizedPath}`;
  }

  return {
    subdomain,
    shouldOverrideSubdomain: shouldOverrideSubdomain(subdomain),
    url,
    referrer,
    userAgent,
    nonce,
    ip,
    path,
    anonymousId,
    segmentAnonymousId,
  };
};

/**
 * subdomains
 */
function shouldOverrideSubdomain(subdomain: string) {
  return ALLOWED_OVERRIDE_SUBDOMAINS.includes(subdomain);
}

function getValidSubdomainName(hostname: string): string {
  const parts = hostname.split('.');

  let subdomain;
  if (hostname.includes('localhost') && parts.length === 2) {
    subdomain = parts[0];
  } else if (parts.length === 3) {
    // remote environments, www.chime.com
    subdomain = parts[0];
  }

  if (subdomain) {
    // Check if it's an allowed subdomain or a non-prod variant and return only baseSubdomain
    const baseSubdomain = subdomain.replace(NON_PROD_SUBDOMAIN_SUFFIX_REGEX, '');
    if (shouldOverrideSubdomain(baseSubdomain)) {
      return baseSubdomain;
    }
  }

  return DEFAULT_SUBDOMAIN;
}
